import linearScale from "./math/linearScale";

/**
 * Plateau-freier Zeitwert fuer scroll-getriebene Videos.
 *
 * HINTERGRUND (Jans Fund 04.08.2026: "bleibt exakt auf dem Slide fuer eine
 * kurze Zeit stehen, bevor es dann weiterlaeuft ... gibt es da eine Loesung,
 * dass das Video nicht einrastet"):
 *
 * Der normale Zeitgeber ist bewusst ungleichmaessig. Er laeuft von
 * `animateAreaFrom` bis `textCenter` und KLEMMT dort fest, solange die Slide
 * zum Lesen stehenbleibt:
 *
 *     linearScale([animateAreaFrom, textCenter], [prevFrom, slideFrom], { clamp: true })
 *
 * Nachgemessen: ueber 800 Pixel Scrollen stand der Zeitgeber exakt still, und
 * das Video mit ihm. Jans Beobachtung dazu passt genau — die Ruhezone entsteht
 * nur, wo eine Textbox Lesezeit braucht; Loop-Videos waren nie betroffen, weil
 * sie nicht am Zeitgeber haengen.
 *
 * DIESE FUNKTION verteilt dieselbe Zeitspanne stattdessen ueber die GESAMTE
 * Scrollstrecke der Slide — von `animateAreaFrom` bis zum Beginn des naechsten
 * Animationsbereichs (bei der letzten Slide bis zu ihrem Ende). An den
 * Slide-Grenzen ist das Ergebnis identisch mit dem echten Zeitgeber, dazwischen
 * laeuft es gleichmaessig weiter statt zu stehen.
 *
 * NUR fuer Scroll-Videos. Alle anderen Animationen haengen weiter am echten
 * Zeitgeber — sonst waere die Lesepause fuer die ganze Szene weg.
 */
export interface SmoothTimerArgs {
    scrollTop: number;
    /** Beginn des Animationsbereichs der aktuellen Slide. */
    animateAreaFrom: number;
    /** Oberkante + Hoehe der aktuellen Slide — Ende, wenn keine naechste folgt. */
    top: number;
    height: number;
    /** Beginn des Animationsbereichs der NAECHSTEN Slide, falls vorhanden. */
    nextAnimateAreaFrom?: number;
    /** Zeitgeber-Wert am Anfang der Spanne (Slide davor). */
    prevFrom: number;
    /** Zeitgeber-Wert am Ende der Spanne (aktuelle Slide). */
    slideFrom: number;
    /** Rueckfall, wenn sich keine brauchbare Scrollstrecke ergibt. */
    fallback: number;
}

export function computeScrollSmoothTimer(a: SmoothTimerArgs): number {
    const ende = a.nextAnimateAreaFrom ?? a.top + a.height;
    // Ohne echte Strecke (entartete Slide, Messung vor dem ersten Layout)
    // waere die Abbildung sinnlos — dann lieber der normale Zeitgeber.
    if (!(ende > a.animateAreaFrom)) return a.fallback;
    if (!Number.isFinite(a.scrollTop)) return a.fallback;
    return linearScale([a.animateAreaFrom, ende], [a.prevFrom, a.slideFrom], {
        clamp: true,
    })(a.scrollTop);
}
